import { useToast } from '@chakra-ui/react';
import { CargoType } from '@pelicargo/types';
import { useMemo } from 'react';

import {
  AirlineBookingSection,
  AirlineBookingValues,
  BookingType,
  useAirlineBookingForm,
} from '../../hooks/emails/useAirlineBookingForm';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import { ContactTags } from '../../utils/constants';
import { sendEmailsToValidProviders } from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { SimpleSharedEmailForm } from '../Emails/SimpleSharedEmailForm';
import { useRequestDrawersContext } from '../RequestDrawers';
import { SimpleAirlineRecipientForm } from '../SimpleAirlineRecipientForm';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';

export const AirlineBookingDrawer = () => {
  const toast = useToast();
  const { request, quotes } = useCurrentRequest();
  const { sendAirlineBookingEmail } = useEmail();

  const { methods } = useAirlineBookingForm(request, quotes, BookingType.Request);

  const { getIsModalOpen, closeModal } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('AirlineBooking');
  const onClose = () => closeModal('AirlineBooking');

  const sendSingleEmail = async (
    provider: AirlineBookingValues['providers'][0],
    data: Omit<AirlineBookingValues, 'providers'>,
  ) => {
    if (!provider?.isExpanded || !provider?.recipients?.length) return;

    const { id: requestId } = request;
    const { quoteId, recipients } = provider;
    const [toContact, ...ccContacts] = recipients;
    const { senderContactId, ...overrides } = data;

    await sendAirlineBookingEmail({
      requestId,
      quoteId,
      senderContactId,
      toContact,
      ccContacts,
      overrides,
    });
  };

  const onValid = async (data: AirlineBookingSection) => {
    try {
      const { airlines } = data;

      // Send emails for all provider types if they have data
      const promises = [];

      if (airlines?.providers?.length) {
        const { providers, ...airlineData } = airlines;
        promises.push(sendEmailsToValidProviders(airlineData, providers as any, sendSingleEmail));
      }

      await Promise.all(promises);
      toast({ title: 'Success', description: 'Emails sent successfully', status: 'success' });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const onError = (errors: any) => {
    console.log('onError', errors);
    toast({ title: 'Error', description: 'Unknown error', status: 'error' });
  };

  const handleSubmit = methods.handleSubmit(onValid, onError);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const defaultTags = useMemo(() => {
    const tags = [ContactTags.Bookings];
    if (request?.cargo_type === CargoType.Perishable) {
      tags.push(ContactTags.TempControl);
    }

    return tags;
  }, [request?.cargo_type]);

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <form onSubmit={handleSubmit}>
        <EmailDrawer isOpen={isOpen} onClose={handleClose}>
          <EmailDrawerLeftPanel
            title="Select Airline"
            description="Select the airline and specify the contacts you want to send the email to"
          >
            <SimpleFieldArray
              name="airlines.providers"
              canAdd={false}
              canRemove={false}
              ignoreStyles
              form={({ prefix }) => (
                <SimpleAirlineRecipientForm
                  defaultAccount={request?.account}
                  prefix={prefix}
                  defaultTags={defaultTags}
                  searchPattern="AIRLINE"
                />
              )}
            />
          </EmailDrawerLeftPanel>
          <EmailDrawerRightPanel title="Book Quote">
            <SimpleSharedEmailForm name="airlines" onSubmit={handleSubmit} onCancel={handleClose} />
          </EmailDrawerRightPanel>
        </EmailDrawer>
      </form>
    </SimpleForm>
  );
};
